/**
 * JourneyStatsPanel — Emotional "Journey So Far" dashboard for
 * Spinal Healing & Recovery.
 *
 * Pulls from /api/healing/spinal-ease/journey-stats and renders:
 *   · A soft one-line reflection (personal to the user's practice)
 *   · Five headline stats (hours relaxed · sessions · family sessions ·
 *     streak · little victories)
 *   · A "Little victories" list — moments of ease, not milestones
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, Sparkles, Users, Flame, Heart } from 'lucide-react';
import TappableSpineWidget from './TappableSpineWidget';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const token = localStorage.getItem('reclaim_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Compact hours label — always shows something loving even if <1h.
const hoursLabel = (hours, minutes) => {
  if (!minutes) return '—';
  if (hours >= 1) return `${hours}`;
  return `${minutes}m`;
};

const StatChip = ({ Icon, label, value, tint, testid, sub }) => (
  <div
    className="rounded-2xl p-4"
    style={{
      background: `linear-gradient(135deg, ${tint} 0%, rgba(255,255,255,0.01) 100%)`,
      border: `1px solid ${tint.replace(/0\.\d+\)$/, '0.22)')}`,
    }}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-1.5">
      <Icon size={11} style={{ color: tint.replace(/0\.\d+\)$/, '0.9)') }} />
      <p
        className="text-white/45 text-[9.5px] tracking-[0.22em] uppercase"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        {label}
      </p>
    </div>
    <p
      className="text-white/90 text-[22px] leading-none"
      style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
    >
      {value}
    </p>
    {sub && (
      <p
        className="text-white/35 text-[10px] mt-1.5"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        {sub}
      </p>
    )}
  </div>
);

const JourneyStatsPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/healing/spinal-ease/journey-stats`, { headers: authHeader() });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setStats(d);
        }
      } catch { /* graceful — the trend below still shows */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !stats) return null;

  const hoursDisplay = hoursLabel(stats.hours_relaxed, stats.minutes_relaxed);
  const hoursSub = stats.hours_relaxed >= 1 ? 'hours' : 'so far';

  return (
    <motion.div
      className="mb-8"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9 }}
      data-testid="journey-stats-panel"
    >
      {/* Reflection */}
      {stats.reflection && (
        <motion.p
          className="text-white/70 text-[13.5px] leading-relaxed mb-6 px-1"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 300 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1.1 }}
          data-testid="journey-reflection"
        >
          {stats.reflection}
        </motion.p>
      )}

      {/* Tappable spine — Michael speaks to whichever vertebra she taps */}
      <TappableSpineWidget />

      {/* Stat grid */}
      <div className="grid grid-cols-3 gap-2.5 mb-3">
        <StatChip
          Icon={Clock}
          label="Relaxed"
          value={hoursDisplay}
          sub={hoursSub}
          tint="rgba(220,190,130,0.09)"
          testid="stat-hours-relaxed"
        />
        <StatChip
          Icon={Sparkles}
          label="Sessions"
          value={stats.sessions_count || 0}
          sub={stats.sessions_count === 1 ? 'complete' : 'completed'}
          tint="rgba(200,170,110,0.09)"
          testid="stat-sessions-count"
        />
        <StatChip
          Icon={Flame}
          label="Streak"
          value={stats.streak_days || 0}
          sub={stats.streak_days === 1 ? 'day' : 'days'}
          tint="rgba(230,150,120,0.09)"
          testid="stat-streak"
        />
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <StatChip
          Icon={Users}
          label="Held by family"
          value={stats.family_sessions || 0}
          sub={stats.family_sessions === 1 ? 'session' : 'sessions'}
          tint="rgba(180,150,220,0.09)"
          testid="stat-family-sessions"
        />
        <StatChip
          Icon={Heart}
          label="Little victories"
          value={stats.victories_count || 0}
          sub="moments of ease"
          tint="rgba(180,220,180,0.09)"
          testid="stat-victories-count"
        />
      </div>

      {/* Little victories list */}
      {stats.little_victories && stats.little_victories.length > 0 && (
        <div className="mt-6">
          <p
            className="text-white/45 text-[10px] tracking-[0.3em] uppercase mb-3"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Little victories
          </p>
          <div className="space-y-2">
            {stats.little_victories.slice(0, 5).map((v, i) => (
              <motion.div
                key={`${v.date}-${i}`}
                className="rounded-xl px-4 py-3 flex items-start gap-3"
                style={{
                  background: 'rgba(180,220,180,0.03)',
                  border: '1px solid rgba(180,220,180,0.12)',
                }}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.08, duration: 0.6 }}
                data-testid={`victory-${i}`}
              >
                <span
                  className="mt-0.5 shrink-0 w-1.5 h-1.5 rounded-full"
                  style={{ background: 'rgba(180,220,180,0.75)' }}
                />
                <div className="flex-1 min-w-0">
                  <p
                    className="text-white/80 text-[12.5px] leading-snug"
                    style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                  >
                    {v.kind === 'pain-eased'
                      ? `Pain eased by ${v.delta} from where it started`
                      : (v.note || 'A moment of gentleness')}
                  </p>
                  <p
                    className="text-white/30 text-[10.5px] mt-1"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {v.date}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Journey length breakdown — only shown if user has tried multiple */}
      {stats.length_counts && (stats.length_counts['flare-up'] + stats.length_counts.daily + stats.length_counts.sleep) > 0 && (
        <div className="mt-6">
          <p
            className="text-white/45 text-[10px] tracking-[0.3em] uppercase mb-3"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Journey rhythm
          </p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl px-2 py-3" style={{ background: 'rgba(230,170,150,0.04)', border: '1px solid rgba(230,170,150,0.15)' }}>
              <p className="text-white/80 text-[18px]" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                {stats.length_counts['flare-up']}
              </p>
              <p className="text-white/40 text-[9.5px] mt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Flare-Ups (5m)
              </p>
            </div>
            <div className="rounded-xl px-2 py-3" style={{ background: 'rgba(220,190,130,0.04)', border: '1px solid rgba(220,190,130,0.15)' }}>
              <p className="text-white/80 text-[18px]" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                {stats.length_counts['daily']}
              </p>
              <p className="text-white/40 text-[9.5px] mt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Daily (20m)
              </p>
            </div>
            <div className="rounded-xl px-2 py-3" style={{ background: 'rgba(170,200,240,0.04)', border: '1px solid rgba(170,200,240,0.15)' }}>
              <p className="text-white/80 text-[18px]" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                {stats.length_counts['sleep']}
              </p>
              <p className="text-white/40 text-[9.5px] mt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Sleep (45m)
              </p>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default JourneyStatsPanel;
